import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FlagIcon from '@mui/icons-material/Flag';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { assignmentsApi, coursesApi, orgApi, resultsApi } from '../../api/client';

type Props = { storeId?: string | null };

const PASS_FAIL_COLORS = { Passed: '#2e7d32', Failed: '#d32f2f' };
const SCORE_RANGE_COLORS: Record<string, string> = {
  '0-49': '#d32f2f',
  '50-59': '#ed6c02',
  '60-69': '#0288d1',
  '70-79': '#66bb6a',
  '80-100': '#2e7d32',
};
const QUESTION_RANGE_COLORS: Record<string, string> = {
  '0-49%': '#d32f2f',
  '50-69%': '#ed6c02',
  '70-89%': '#1976d2',
  '90-100%': '#2e7d32',
};

function tooltipNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function CourseTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const title = payload[0]?.payload?.courseTitle || '';
  return (
    <Box sx={{ bgcolor: 'white', border: '1px solid #ccc', borderRadius: 1, p: 1.25, boxShadow: 1 }}>
      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>{title}</Typography>
      {payload.map((entry: any) => (
        <Typography key={entry.name} variant="body2" sx={{ color: entry.color }}>
          {entry.name}: {entry.value}
        </Typography>
      ))}
    </Box>
  );
}

function downloadFile(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

function toCsv(rows: (string | number)[][]) {
  return rows
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

function toExcelXml(sheets: { name: string; rows: (string | number)[][] }[]) {
  const escape = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const body = sheets
    .map((sheet) => {
      const rows = sheet.rows
        .map((row) => `<Row>${row.map((cell) => `<Cell><Data ss:Type="String">${escape(String(cell))}</Data></Cell>`).join('')}</Row>`)
        .join('');
      return `<Worksheet ss:Name="${escape(sheet.name)}"><Table>${rows}</Table></Worksheet>`;
    })
    .join('');
  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
${body}
</Workbook>`;
}

function answerAt(answers: any, index: number, questionId?: string) {
  if (answers == null) return null;
  if (Array.isArray(answers)) {
    const value = answers[index];
    if (value == null || value === '' || value === -1) return null;
    return value;
  }
  if (questionId && typeof answers === 'object' && answers[questionId] != null && answers[questionId] !== '') {
    return answers[questionId];
  }
  return null;
}

const QuizAnalyticsPage: React.FC<Props> = ({ storeId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [questionFilter, setQuestionFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [employeeRows, assignmentRows, resultRows, courseRows] = await Promise.all([
        orgApi.listEmployees(storeId ? { storeId } : undefined),
        assignmentsApi.list(),
        resultsApi.list(),
        coursesApi.list(),
      ]);
      const employeeIds = new Set((employeeRows as any[]).map((employee) => employee.id));
      const scopedAssignments = storeId
        ? (assignmentRows as any[]).filter((item) => employeeIds.has(item.employeeId))
        : (assignmentRows as any[]);
      const assignmentIds = new Set(scopedAssignments.map((item) => item.id));
      const scopedResults = (resultRows as any[]).filter((item) => assignmentIds.has(item.assignmentId));
      setEmployees(employeeRows as any[]);
      setAssignments(scopedAssignments);
      setResults(scopedResults);
      setCourses(courseRows as any[]);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load quiz analytics');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    load();
  }, [load]);

  const departments = useMemo(
    () => Array.from(new Set(employees.map((employee) => employee.department).filter(Boolean))).sort(),
    [employees],
  );

  const filteredResults = useMemo(() => {
    let rows = results;
    if (dateRangeStart) {
      const start = new Date(dateRangeStart).getTime();
      rows = rows.filter((row) => new Date(row.createdAt).getTime() >= start);
    }
    if (dateRangeEnd) {
      const end = new Date(`${dateRangeEnd}T23:59:59`).getTime();
      rows = rows.filter((row) => new Date(row.createdAt).getTime() <= end);
    }
    if (departmentFilter !== 'all') {
      const employeeIds = new Set(employees.filter((employee) => employee.department === departmentFilter).map((employee) => employee.id));
      const assignmentIds = new Set(assignments.filter((item) => employeeIds.has(item.employeeId)).map((item) => item.id));
      rows = rows.filter((row) => assignmentIds.has(row.assignmentId));
    }
    if (courseFilter !== 'all') {
      const assignmentIds = new Set(assignments.filter((item) => item.courseId === courseFilter).map((item) => item.id));
      rows = rows.filter((row) => assignmentIds.has(row.assignmentId));
    }
    return rows;
  }, [results, dateRangeStart, dateRangeEnd, departmentFilter, courseFilter, assignments, employees]);

  const assignmentById = useMemo(() => new Map(assignments.map((item) => [item.id, item])), [assignments]);

  const courseAnalytics = useMemo(() => {
    const byCourse = new Map<string, any>();
    filteredResults.forEach((result) => {
      const assignment = assignmentById.get(result.assignmentId);
      const course = assignment?.course;
      if (!assignment || !course) return;
      if (!byCourse.has(course.id)) {
        byCourse.set(course.id, {
          courseId: course.id,
          courseTitle: course.title,
          totalAttempts: 0,
          results: [] as any[],
        });
      }
      const row = byCourse.get(course.id);
      row.results.push(result);
      row.totalAttempts += 1;
    });

    return Array.from(byCourse.values())
      .map((row) => {
        const totalScore = row.results.reduce((sum: number, result: any) => sum + Number(result.score || 0), 0);
        const passed = row.results.filter((result: any) => result.passed).length;
        const employeeAttempts = new Map<string, number>();
        row.results.forEach((result: any) => {
          const assignment = assignmentById.get(result.assignmentId);
          if (!assignment) return;
          employeeAttempts.set(assignment.employeeId, (employeeAttempts.get(assignment.employeeId) || 0) + 1);
        });
        const distribution: Record<string, number> = {};
        row.results.forEach((result: any) => {
          const range = Math.floor(Number(result.score || 0) / 10) * 10;
          const key = `${range}-${range + 9}`;
          distribution[key] = (distribution[key] || 0) + 1;
        });
        return {
          ...row,
          averageScore: row.results.length ? Math.round(totalScore / row.results.length) : 0,
          passRate: row.results.length ? Math.round((passed / row.results.length) * 100) : 0,
          averageAttemptsPerEmployee: employeeAttempts.size
            ? Math.round((row.totalAttempts / employeeAttempts.size) * 100) / 100
            : 0,
          scoreDistribution: Object.entries(distribution)
            .map(([score, count]) => ({ score, count }))
            .sort((a, b) => parseInt(a.score, 10) - parseInt(b.score, 10)),
        };
      })
      .sort((a, b) => b.totalAttempts - a.totalAttempts);
  }, [filteredResults, assignmentById]);

  const questionAnalytics = useMemo(() => {
    const courseStats = new Map(courseAnalytics.map((row) => [row.courseId, row]));
    const resultsByCourse = new Map<string, any[]>();
    filteredResults.forEach((result) => {
      const assignment = assignmentById.get(result.assignmentId);
      if (!assignment?.courseId) return;
      const list = resultsByCourse.get(assignment.courseId) || [];
      list.push(result);
      resultsByCourse.set(assignment.courseId, list);
    });

    const rows: any[] = [];
    courses.forEach((course) => {
      const courseResults = resultsByCourse.get(course.id) || [];
      if (!courseResults.length) return;
      const stats = courseStats.get(course.id);
      (course.quiz || [])
        .filter((question: any) => {
          const type = String(question.questionType || 'multiple_choice').toLowerCase();
          return !['fill_blank', 'fill_in_blank', 'fill-in-blank', 'fillinblank'].includes(type);
        })
        .forEach((question: any, index: number) => {
          let known = 0;
          let correctFromAnswers = 0;
          courseResults.forEach((result) => {
            const answer = answerAt(result.answers, index, question.id);
            if (answer == null) return;
            known += 1;
            const numeric = Number(answer);
            const choice = question.questionType !== 'short_answer';
            const correct = choice
              ? numeric === question.correctAnswer
              : String(answer).trim().toLowerCase() === String(question.correctAnswerText || '').trim().toLowerCase();
            if (correct) correctFromAnswers += 1;
          });

          const totalAttempts = courseResults.length;
          const estimatedRate = stats ? Math.max(0, Math.min(100, stats.averageScore)) : 0;
          const correctCount = known > 0 ? correctFromAnswers : Math.round((estimatedRate / 100) * totalAttempts);
          const counted = known > 0 ? known : totalAttempts;
          const incorrectCount = Math.max(0, counted - correctCount);
          const successRate = counted > 0 ? Math.round((correctCount / counted) * 100) : 0;
          rows.push({
            questionId: question.id,
            questionText: question.question,
            courseTitle: course.title,
            successRate,
            totalAttempts,
            correctCount,
            incorrectCount,
            isFlagged: successRate < 50 || successRate === 100,
            flagReason: successRate < 50 ? 'difficult' : successRate === 100 ? 'easy' : null,
          });
        });
    });
    return rows.sort((a, b) => a.successRate - b.successRate);
  }, [courses, filteredResults, assignmentById, courseAnalytics]);

  const visibleQuestions = questionFilter === 'flagged'
    ? questionAnalytics.filter((row) => row.isFlagged)
    : questionAnalytics;

  const passFailData = useMemo(() => {
    const passed = filteredResults.filter((row) => row.passed).length;
    const failed = filteredResults.length - passed;
    return [
      { name: 'Passed', value: passed, color: PASS_FAIL_COLORS.Passed },
      { name: 'Failed', value: failed, color: PASS_FAIL_COLORS.Failed },
    ].filter((item) => item.value > 0);
  }, [filteredResults]);

  const scoreRangeData = useMemo(() => {
    const ranges = [
      { name: '0-49', min: 0, max: 49 },
      { name: '50-59', min: 50, max: 59 },
      { name: '60-69', min: 60, max: 69 },
      { name: '70-79', min: 70, max: 79 },
      { name: '80-100', min: 80, max: 100 },
    ];
    return ranges
      .map((range) => ({
        name: range.name,
        value: filteredResults.filter((row) => Number(row.score) >= range.min && Number(row.score) <= range.max).length,
        color: SCORE_RANGE_COLORS[range.name],
      }))
      .filter((item) => item.value > 0);
  }, [filteredResults]);

  const questionRangeData = useMemo(() => {
    const ranges = [
      { name: '0-49%', min: 0, max: 49 },
      { name: '50-69%', min: 50, max: 69 },
      { name: '70-89%', min: 70, max: 89 },
      { name: '90-100%', min: 90, max: 100 },
    ];
    return ranges
      .map((range) => ({
        name: range.name,
        value: questionAnalytics.filter((row) => row.successRate >= range.min && row.successRate <= range.max).length,
        color: QUESTION_RANGE_COLORS[range.name],
      }))
      .filter((item) => item.value > 0);
  }, [questionAnalytics]);

  const departmentPerformance = useMemo(() => {
    const map = new Map<string, { totalScore: number; count: number; passed: number }>();
    filteredResults.forEach((result) => {
      const assignment = assignmentById.get(result.assignmentId);
      const department = assignment?.employee?.department;
      if (!department) return;
      const current = map.get(department) || { totalScore: 0, count: 0, passed: 0 };
      current.totalScore += Number(result.score || 0);
      current.count += 1;
      if (result.passed) current.passed += 1;
      map.set(department, current);
    });
    return Array.from(map.entries())
      .map(([department, data]) => ({
        department,
        averageScore: data.count ? Math.round(data.totalScore / data.count) : 0,
        passRate: data.count ? Math.round((data.passed / data.count) * 100) : 0,
      }))
      .sort((a, b) => b.averageScore - a.averageScore);
  }, [filteredResults, assignmentById]);

  const scoreDistribution = useMemo(() => {
    const buckets = new Map<string, number>();
    filteredResults.forEach((result) => {
      const range = Math.floor(Number(result.score || 0) / 10) * 10;
      const key = `${range}-${range + 9}`;
      buckets.set(key, (buckets.get(key) || 0) + 1);
    });
    return Array.from(buckets.entries())
      .map(([score, count]) => ({ score, count }))
      .sort((a, b) => parseInt(a.score, 10) - parseInt(b.score, 10));
  }, [filteredResults]);

  const trendData = useMemo(
    () => [...filteredResults]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .map((row, index) => ({ attempt: index + 1, score: Number(row.score || 0) })),
    [filteredResults],
  );

  const overallAverageScore = filteredResults.length
    ? Math.round(filteredResults.reduce((sum, row) => sum + Number(row.score || 0), 0) / filteredResults.length)
    : 0;
  const overallPassRate = filteredResults.length
    ? Math.round((filteredResults.filter((row) => row.passed).length / filteredResults.length) * 100)
    : 0;

  const exportRows = () => {
    const courseRows: (string | number)[][] = [
      ['Course Title', 'Average Score', 'Pass Rate (%)', 'Total Attempts', 'Avg Attempts per Employee'],
      ...courseAnalytics.map((row) => [row.courseTitle, row.averageScore, row.passRate, row.totalAttempts, row.averageAttemptsPerEmployee]),
    ];
    const questionRows: (string | number)[][] = [
      ['Course', 'Question', 'Success Rate (%)', 'Total Attempts', 'Correct Count', 'Incorrect Count', 'Flagged'],
      ...questionAnalytics.map((row) => [
        row.courseTitle,
        row.questionText,
        row.successRate,
        row.totalAttempts,
        row.correctCount,
        row.incorrectCount,
        row.isFlagged ? (row.flagReason === 'difficult' ? 'Difficult' : 'Too Easy') : 'No',
      ]),
    ];
    return { courseRows, questionRows };
  };

  const exportExcel = () => {
    const { courseRows, questionRows } = exportRows();
    downloadFile(
      'Quiz_Analytics.xls',
      toExcelXml([
        { name: 'Course Analytics', rows: courseRows },
        { name: 'Question Analytics', rows: questionRows },
      ]),
      'application/vnd.ms-excel',
    );
  };

  const exportCsv = () => {
    const { courseRows, questionRows } = exportRows();
    downloadFile('Quiz_Analytics.csv', `Course Analytics\n${toCsv(courseRows)}\n\nQuestion Analytics\n${toCsv(questionRows)}`, 'text/csv');
  };

  const pieLegend = (total: number) => (value: string, entry: any) => {
    const count = entry?.payload?.value ?? 0;
    const percent = total > 0 ? Math.round((count / total) * 100) : 0;
    return `${value}: ${count} (${percent}%)`;
  };

  const donut = (title: string, data: { name: string; value: number; color: string }[], unit: string) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    return (
      <Grid item xs={12} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{title}</Typography>
            {data.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>No data yet</Typography>
            ) : (
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data} dataKey="value" nameKey="name" innerRadius={58} outerRadius={88} paddingAngle={2} cy="42%">
                      {data.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(value) => {
                      const count = tooltipNumber(value);
                      const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                      return [`${count} (${percent}%)`, unit];
                    }} />
                    <Legend verticalAlign="bottom" formatter={pieLegend(total)} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h4">Quiz Performance Analytics</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton color="primary" title="Refresh" onClick={load}>
            <RefreshIcon />
          </IconButton>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportExcel}>Export Excel</Button>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportCsv}>Export CSV</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" label="Start Date" type="date" value={dateRangeStart} onChange={(e) => setDateRangeStart(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField fullWidth size="small" label="End Date" type="date" value={dateRangeEnd} onChange={(e) => setDateRangeEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select value={departmentFilter} label="Department" onChange={(e) => setDepartmentFilter(e.target.value)}>
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map((department) => <MenuItem key={department} value={department}>{department}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Course</InputLabel>
                <Select value={courseFilter} label="Course" onChange={(e) => setCourseFilter(e.target.value)}>
                  <MenuItem value="all">All Courses</MenuItem>
                  {courses.map((course) => <MenuItem key={course.id} value={course.id}>{course.title}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          ['Overall Average Score', `${overallAverageScore}%`],
          ['Overall Pass Rate', `${overallPassRate}%`],
          ['Total Quiz Attempts', filteredResults.length],
          ['Courses Analyzed', courseAnalytics.length],
        ].map(([label, value]) => (
          <Grid item xs={12} sm={6} md={3} key={String(label)}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2" gutterBottom>{label}</Typography>
                <Typography variant="h4">{value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {donut('Pass/Fail Distribution', passFailData, 'Attempts')}
        {donut('Score Range Distribution', scoreRangeData, 'Attempts')}
        {donut('Question Success Rate Distribution', questionRangeData, 'Questions')}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Average Score by Course</Typography>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={courseAnalytics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="courseTitle" tick={false} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip content={<CourseTooltip />} />
                    <Legend />
                    <Bar dataKey="averageScore" fill="#1976d2" name="Average Score (%)" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Department Performance</Typography>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={departmentPerformance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="department" angle={-35} textAnchor="end" height={70} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="averageScore" fill="#1976d2" name="Average Score (%)" />
                    <Bar dataKey="passRate" fill="#2e7d32" name="Pass Rate (%)" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Total Attempts by Course</Typography>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={courseAnalytics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="courseTitle" tick={false} />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={<CourseTooltip />} />
                    <Legend />
                    <Bar dataKey="totalAttempts" fill="#1976d2" name="Total Attempts" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Average Attempts per Employee by Course</Typography>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={courseAnalytics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="courseTitle" tick={false} />
                    <YAxis />
                    <Tooltip content={<CourseTooltip />} />
                    <Legend />
                    <Bar dataKey="averageAttemptsPerEmployee" fill="#e91e63" name="Avg Attempts/Employee" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Course-Level Analytics</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Course</TableCell>
                  <TableCell align="right">Average Score</TableCell>
                  <TableCell align="right">Pass Rate</TableCell>
                  <TableCell align="right">Total Attempts</TableCell>
                  <TableCell align="right">Avg Attempts/Employee</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {courseAnalytics.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No data available</TableCell>
                  </TableRow>
                ) : courseAnalytics.map((row) => (
                  <TableRow key={row.courseId}>
                    <TableCell>{row.courseTitle}</TableCell>
                    <TableCell align="right">
                      <Typography color={row.averageScore >= 80 ? 'success.main' : row.averageScore >= 60 ? 'warning.main' : 'error.main'} fontWeight={700}>
                        {row.averageScore}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.passRate}%</TableCell>
                    <TableCell align="right">{row.totalAttempts}</TableCell>
                    <TableCell align="right">{Number(row.averageAttemptsPerEmployee).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Score Distribution</Typography>
          <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={scoreDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="score" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#1976d2" name="count" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6">Question-Level Analytics</Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select value={questionFilter} onChange={(e) => setQuestionFilter(e.target.value)}>
                <MenuItem value="all">All Questions</MenuItem>
                <MenuItem value="flagged">Flagged Questions Only</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Course</TableCell>
                  <TableCell>Question</TableCell>
                  <TableCell align="right">Success Rate</TableCell>
                  <TableCell align="right">Total Attempts</TableCell>
                  <TableCell align="right">Correct</TableCell>
                  <TableCell align="right">Incorrect</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleQuestions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No questions found</TableCell>
                  </TableRow>
                ) : visibleQuestions.map((row) => (
                  <TableRow
                    key={row.questionId}
                    sx={{ bgcolor: row.flagReason === 'difficult' ? 'error.light' : row.flagReason === 'easy' ? 'info.light' : 'inherit' }}
                  >
                    <TableCell>{row.courseTitle}</TableCell>
                    <TableCell>{row.questionText}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} color={row.successRate < 50 ? 'error.main' : row.successRate === 100 ? 'info.main' : 'text.primary'}>
                        {row.successRate}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.totalAttempts}</TableCell>
                    <TableCell align="right">{row.correctCount}</TableCell>
                    <TableCell align="right">{row.incorrectCount}</TableCell>
                    <TableCell>
                      {row.isFlagged && (
                        <Chip
                          icon={<FlagIcon />}
                          label={row.flagReason === 'difficult' ? 'Difficult' : 'Too Easy'}
                          color={row.flagReason === 'difficult' ? 'error' : 'info'}
                          size="small"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Pass Rate by Course</Typography>
          <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={courseAnalytics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="courseTitle" tick={false} />
                <YAxis domain={[0, 100]} />
                <Tooltip content={<CourseTooltip />} />
                <Legend />
                <Bar dataKey="passRate" fill="#2e7d32" name="Pass Rate (%)" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Score Trends Over Time</Typography>
          <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="attempt" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="score" stroke="#1976d2" name="Score (%)" dot />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default QuizAnalyticsPage;
